import React, { useContext } from 'react';
import { ControlProviderFilterSelect } from '../../../components/sidebar-filters/filter-select/ControlProviderFilterSelect';
import { INominationsSidebarFiltersContext, INominationsSidebarFilters, NominationSidebarFiltersContext } from './NominationSidebarFiltersContext';
import { IsoFilterSelect } from '../../../components/sidebar-filters/filter-select/IsoFilterSelect';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { CollapseFilterWrapper } from '../../../components/sidebar-filters/CollapseFilterWrapper';
import { ProgramUtilityFilterSelect } from '../../../components/sidebar-filters/filter-select/ProgramUtilityFilterSelect';
import { MarketFilterSelect } from '../../../components/sidebar-filters/filter-select/MarketFilterSelect';
import { ProgramFilterSelect } from '../../../components/sidebar-filters/filter-select/ProgramFilterSelect';
import { MeterEnrollmentStatusFilterSelect } from '../../../components/sidebar-filters/filter-select/MeterEnrollmentStatusFilterSelect';
import { NominationStatusFilterSelect } from '../../../components/sidebar-filters/filter-select/NominationStatusFilterSelect';
import { NominationRecencyFilterSelect } from '../../../components/sidebar-filters/filter-select/NominationRecencyFilterSelect';
import { AccountManagerFilterSelect } from '../../../components/sidebar-filters/filter-select/AccountManagerFilterSelect';
import { useAuth } from '../../../domain/auth/useAuth';
import { SiteFilterSelect } from '../../../components/sidebar-filters/filter-select/SiteFilterSelect';
import { EnrollmentIdFilterSelect } from '../../../components/sidebar-filters/filter-select/EnrollmentIdFilterSelect';

export const NominationFilterKeys = {
    // Filters
    accountManager: 'account_manager',
    iso: 'iso',
    utility: 'utility_id',
    market: 'lmp_market',
    program: 'program_name',
    controlProvider: 'control_provider_name',
    enrollmentStatus: 'enrollment_status',
    nominationRecency: 'nomination_recency',
    nominationStatus: 'nomination_status',
    siteId: 'site_id',
    enrollmentId: 'enrollment_id',
} as const;

export type TNominationFilterKey = (typeof NominationFilterKeys)[keyof typeof NominationFilterKeys];

export const NominationFilterLabels = {
    [NominationFilterKeys.accountManager]: 'Account Manager',
    [NominationFilterKeys.iso]: 'ISO',
    [NominationFilterKeys.utility]: 'Utility',
    [NominationFilterKeys.market]: 'Market',
    [NominationFilterKeys.program]: 'Program',
    [NominationFilterKeys.controlProvider]: 'Control Provider',
    [NominationFilterKeys.enrollmentStatus]: 'Meter Enrollment Status',
    [NominationFilterKeys.nominationRecency]: 'Nomination Recency',
    [NominationFilterKeys.nominationStatus]: 'Nomination Status',
    [NominationFilterKeys.siteId]: 'Site',
    [NominationFilterKeys.enrollmentId]: 'Enrollment ID',
} as const;

interface INominationsSidebarFiltersProps {
    onFilterChange: (filters: INominationsSidebarFilters) => void;
}

export const NominationsSidebarFilters: React.FC<INominationsSidebarFiltersProps> = ({ onFilterChange }) => {
    const auth = useAuth()!;
    const { filters, handleFilterChange } = useContext<INominationsSidebarFiltersContext>(NominationSidebarFiltersContext);
    const {
        controlProviderOptions,
        isLoading: isFiltersLoading,
        siteOptions,
        meterEnrollmentEnrollmentIdOptions,
        accountManagerOptions,
        isoOptions,
        programUtilityOptions,
        marketOptions,
        programOptions,
        meterEnrollmentStatusOptions,
        nominationRecencyOptions,
        nominationStatusOptions,
    } = useSidebarFilterOptionsContext();

    const handleNominationsFilterChange = (key: string, value: unknown) => {
        handleFilterChange(key, value);
        onFilterChange({ ...filters, [key]: value });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            {/* Filters */}
            <CollapseFilterWrapper label="Filters">
                <SiteFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.siteId]}
                    value={filters[NominationFilterKeys.siteId] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.siteId, value)}
                    options={siteOptions}
                    isLoading={isFiltersLoading}
                />
                <EnrollmentIdFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.enrollmentId]}
                    value={filters[NominationFilterKeys.enrollmentId] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.enrollmentId, value)}
                    options={meterEnrollmentEnrollmentIdOptions}
                    isLoading={isFiltersLoading}
                />
                <AccountManagerFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.accountManager]}
                    value={filters[NominationFilterKeys.accountManager] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.accountManager, value)}
                    options={accountManagerOptions}
                    isLoading={isFiltersLoading}
                />
                <IsoFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.iso]}
                    value={filters[NominationFilterKeys.iso] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.iso, value)}
                    options={isoOptions}
                />
                <ProgramUtilityFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.utility]}
                    value={filters[NominationFilterKeys.utility] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.utility, value)}
                    options={programUtilityOptions}
                />
                <MarketFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.market]}
                    value={filters[NominationFilterKeys.market] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.market, value)}
                    options={marketOptions}
                    isLoading={isFiltersLoading}
                />
                <ProgramFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.program]}
                    value={filters[NominationFilterKeys.program] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.program, value)}
                    options={programOptions}
                    isLoading={isFiltersLoading}
                />
                <ControlProviderFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.controlProvider]}
                    value={filters[NominationFilterKeys.controlProvider] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.controlProvider, value)}
                    options={controlProviderOptions}
                    isLoading={isFiltersLoading}
                />
                <MeterEnrollmentStatusFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.enrollmentStatus]}
                    value={filters[NominationFilterKeys.enrollmentStatus] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.enrollmentStatus, value)}
                    options={meterEnrollmentStatusOptions}
                />
                <NominationRecencyFilterSelect
                    label={NominationFilterLabels[NominationFilterKeys.nominationRecency]}
                    value={filters[NominationFilterKeys.nominationRecency] || []}
                    onChange={value => handleNominationsFilterChange(NominationFilterKeys.nominationRecency, value)}
                    options={nominationRecencyOptions}
                />
                {auth.user?.isAdminRoleType() && (
                    <NominationStatusFilterSelect
                        label={NominationFilterLabels[NominationFilterKeys.nominationStatus]}
                        value={filters[NominationFilterKeys.nominationStatus] || []}
                        onChange={value => handleNominationsFilterChange(NominationFilterKeys.nominationStatus, value)}
                        options={nominationStatusOptions}
                    />
                )}
            </CollapseFilterWrapper>
        </div>
    );
};
